import { memo, useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import type { ChartData, Chat, InputAttachment, SmartRoutingV2 } from '@trinity/chat';
import { ChatCitationType, ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import { GenericFormWorkflow } from '@trinity/chat-components/components/Chat.Forms/GenericFormWorkflow.js';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container.js';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import type { CustomResponseComponent, CustomResponseInput } from '@trinity/custom-pages-registry';
import { getCustomChatResponseDefinition } from '@trinity/custom-pages-registry';
import { AgentProgressContainer } from '../Chat.AgentProgress/AgentProgressContainer';
import { AgentProgressContext } from '../Chat.AgentProgress/context';
import { Item as AttachmentItem } from '../Chat.Attachments/Item';
import { CustomPageContextContainer } from '../CustomPageContext';
import { BasicMarkdown } from '../Markdown/Markdown';
import { SmartRoutingContainer } from '../SmartRouting/SmartRouting.Container';
import { AudioStatusListener } from './AudioStatusListener';
import { AttachmentsContext, AttachmentItemRendererContext, IsSidePanelMessageContext } from './context';
import { AvailabilityInsightsMessage } from './KolSearch.Profile.Availability';
import { AccountSearchResponse } from './Message.Response.AccountSearch';
import { AccountSearchResultsResponse } from './Message.Response.AccountSearchResults';
import { ChartContainer } from './Message.Response.ChartContainer';
import { HcpSearchResponse } from './Message.Response.HcpSearch';
import { HcpSearchResultsResponse } from './Message.Response.HcpSearchResults';
import { RepSearchResponse } from './Message.Response.RepSearch';
import { RepSearchResultsResponse } from './Message.Response.RepSearchResults';
import { SchedulingAssistantResponse } from './Message.Response.SchedulingAssistant';
import { StreamingSystemUserMessage, SystemUserMessage } from './Message.System';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { SystemUserCancelledMessage } from './Message.System.Cancelled';
import { SystemMessageContainer } from './Message.System.Container';
import { SystemUserErrorMessage } from './Message.System.Error';
import { SystemUserInFlightResponse } from './Message.System.InProgress';
import { MessageWrapper } from './Message.System.Wrapper';
import { UserMessage } from './Message.User';
import { PlannerClarify } from './PlannerClarify';
import { PlannerReview } from './PlannerReview';
import { AccountProfileMessage } from './ProfileMessage/AccountProfile.Message';
import { KolProfileMessage } from './ProfileMessage/HcpProfile.Message';
import { QueryFollowupsContainer } from './Query.FollowupsContainer';
import { QueryEventsContainer } from './QueryEventsContainer';
import { QuerySocketContainer } from './QuerySocketContainer';
import { SegmentationConfigure } from './Segmentation.Configure';
import { SegmentationDimensionSelection } from './Segmentation.DimensionSelection';
import { SegmentationEDA } from './Segmentation.EDA';
import { SegmentationExport } from './Segmentation.Export';
import { SegmentationFeatures } from './Segmentation.Features';
import { SegmentationRefinement } from './Segmentation.Refinement';
import { SegmentationResult, SegmentationResultUserMessage } from './Segmentation.Result';
import { SegmentationResults } from './Segmentation.Results';
import { SegmentationStrategy } from './Segmentation.Strategy';
import { SegmentationTagSelection } from './Segmentation.TagSelection';
import { SegmentationUpload } from './Segmentation.Upload';
import { SegmentChatSelection } from './SegmentChatSelection';

type Props = {
  canRegenerate: boolean;
  query: Chat.Query;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function renderHcpResponses(query: Chat.Query, canRegenerate: boolean) {
  const responseType = query.response?.type;

  if (responseType === ChatResponseType.HcpSelection) {
    throw new Error(`HCP Selection isn't implemented`);
  }

  if (responseType === ChatResponseType.HcpSearch) {
    return <HcpSearchResponse disabled={!canRegenerate} />;
  }

  if (responseType === ChatResponseType.HcpSearchResults) {
    return (
      <HcpSearchResultsResponse
        queryIdentifier={query.identifier}
        response={query.response}
        disabled={!canRegenerate}
      />
    );
  }

  if (responseType === ChatResponseType.HcpRecommendationsSearch) {
    throw new Error(`HCP Recommendations isn't implemented`);
  }

  if (responseType === ChatResponseType.HcpSummary) {
    return (
      <QueryFollowupsContainer queryIdentifier={query.identifier} queryStatus={query.status}>
        <AudioStatusListener queryIdentifier={query.identifier} queryId={query.id}>
          <SystemMessageContainer citations={query.citations}>
            <KolProfileMessage
              followupQuestions={query.suggestedFollowups}
              profile={query.response.data.profile}
              queryId={query.id}
            />
          </SystemMessageContainer>
        </AudioStatusListener>
      </QueryFollowupsContainer>
    );
  }

  if (responseType === ChatResponseType.AccountSummary) {
    return (
      <QueryFollowupsContainer queryIdentifier={query.identifier} queryStatus={query.status}>
        <AudioStatusListener queryIdentifier={query.identifier} queryId={query.id}>
          <SystemMessageContainer citations={query.citations}>
            <AccountProfileMessage
              followupQuestions={query.suggestedFollowups}
              profile={query.response.data.profile}
              queryId={query.id}
              queryIdentifier={query.identifier}
            />
          </SystemMessageContainer>
        </AudioStatusListener>
      </QueryFollowupsContainer>
    );
  }

  if (responseType === ChatResponseType.HcpAvailability) {
    return (
      <MessageContainer>
        <MessageWrapper>
          <SystemMessageContainer
            citations={query.citations.filter((x) => x.type === ChatCitationType.FbmsInteractions)}
          >
            <BasicSystemUserMessage>
              <AvailabilityInsightsMessage
                citations={query.citations}
                profile={query.response.data.profile}
              />
            </BasicSystemUserMessage>
          </SystemMessageContainer>
        </MessageWrapper>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SchedulingAssistant) {
    return (
      <MessageWrapper>
        <BasicSystemUserMessage>
          <SchedulingAssistantResponse
            queryId={query.id}
            state={query.state as Chat.QueryState.SchedulingAssistant}
          />
        </BasicSystemUserMessage>
      </MessageWrapper>
    );
  }

  if (responseType === ChatResponseType.FormWorkflow) {
    return (
      <MessageWrapper>
        <BasicSystemUserMessage>
          <ContextSelectorContainer>
            <GenericFormWorkflow
              key={`form-workflow-${query.id}-${query.identifier}`}
              queryId={query.id}
              queryIdentifier={query.identifier}
              value={query.state}
            />
          </ContextSelectorContainer>
        </BasicSystemUserMessage>
      </MessageWrapper>
    );
  }

  return undefined;
}

function renderCustomResponse(query: Chat.Query) {
  const response = query.response;
  if (!response) {
    return undefined;
  }

  const definition = getCustomChatResponseDefinition(response.type);
  if (!definition || definition.renderLocation !== 'top-level') {
    return undefined;
  }

  const Component = definition.component as CustomResponseComponent;

  return (
    <MessageContainer>
      <MessageWrapper>
        <BasicSystemUserMessage>
          <CustomPageContextContainer>
            <Component
              queryId={query.id}
              queryIdentifier={query.identifier}
              response={response as CustomResponseInput}
            />
          </CustomPageContextContainer>
        </BasicSystemUserMessage>
      </MessageWrapper>
    </MessageContainer>
  );
}

function renderSegmentationBuilderStep(query: Chat.Query) {
  const responseType = query.response?.type;
  const ComponentMap: Partial<
    Record<
      ChatResponseType,
      React.ComponentType<{ queryId: number; queryIdentifier: string; value: unknown }>
    >
  > = {
    [ChatResponseType.SegmentationUpload]: SegmentationUpload,
    [ChatResponseType.SegmentationConfigure]: SegmentationConfigure,
    [ChatResponseType.SegmentationEDA]: SegmentationEDA,
    [ChatResponseType.SegmentationStrategy]: SegmentationStrategy,
    [ChatResponseType.SegmentationRefinement]: SegmentationRefinement,
    [ChatResponseType.SegmentationFeatures]: SegmentationFeatures,
    [ChatResponseType.SegmentationResults]: SegmentationResults,
    [ChatResponseType.SegmentationExport]: SegmentationExport,
  };
  const Component = ComponentMap[responseType];
  if (Component) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <Component
            queryId={query.id}
            queryIdentifier={query.identifier}
            value={query.state}
          />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }
  return undefined;
}

function renderSegmentationResponses(query: Chat.Query, canRegenerate: boolean) {
  const responseType = query.response?.type;

  if (responseType === ChatResponseType.SegmentationTagsNeeded) {
    const content = (
      <MessageContainer>
        <BasicSystemUserMessage>
          <SegmentationTagSelection
            queryId={query.id}
            queryIdentifier={query.identifier}
            value={query.state as Chat.QueryState.SegmentationTagsNeeded}
          />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
    if (!canRegenerate) return content;
    return (
      <QuerySocketContainer queryIdentifier={query.identifier}>
        <QueryEventsContainer queryIdentifier={query.identifier}>{content}</QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatNeeded) {
    // The query is already complete here, so polling would loop (each query-response-available
    // dispatch re-creates the poll callback). Cancel still arrives through the socket event.
    return (
      <QuerySocketContainer queryIdentifier={query.identifier}>
        <QueryEventsContainer queryIdentifier={query.identifier} pollingEnabed={false}>
          <MessageContainer>
            <BasicSystemUserMessage>
              <SegmentChatSelection
                queryIdentifier={query.identifier}
                value={query.state as Chat.QueryState.SegmentChatNeeded}
              />
            </BasicSystemUserMessage>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatCancelled) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>No problem! Let me know how else I can help.</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatSelected) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>{query.response.value}</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationQuestionsNeeded) {
    const state = query.state as Chat.QueryState.SegmentationQuestionsNeeded;
    const selectedTagNames = state.availableTags
      .filter((tag) => state.selectedTags.includes(tag.id))
      .map((tag) => tag.name)
      .join(', ');
    return (
      <QuerySocketContainer queryIdentifier={query.identifier}>
        <QueryEventsContainer queryIdentifier={query.identifier}>
          <MessageContainer>
            <UserMessage value={`I'd like to analyze ${selectedTagNames} tagged data.`} />
          </MessageContainer>
          <MessageContainer>
            <BasicSystemUserMessage>
              <SegmentationDimensionSelection
                queryId={query.id}
                queryIdentifier={query.identifier}
                value={query.state as Chat.QueryState.SegmentationQuestionsNeeded}
              />
            </BasicSystemUserMessage>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationCancelled) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>Segmentation has been cancelled.</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationComplete) {
    return (
      <QuerySocketContainer queryIdentifier={query.identifier}>
        <QueryEventsContainer queryIdentifier={query.identifier}>
          <MessageContainer>
            <UserMessage
              value={
                <SegmentationResultUserMessage
                  queryId={query.id}
                  queryIdentifier={query.identifier}
                  value={query.state as Chat.QueryState.SegmentationComplete}
                  citations={query.citations}
                />
              }
            />
          </MessageContainer>
          <MessageContainer>
            <SystemMessageContainer citations={query.citations}>
              <BasicSystemUserMessage>
                <SegmentationResult
                  queryId={query.id}
                  queryIdentifier={query.identifier}
                  value={query.state as Chat.QueryState.SegmentationComplete}
                  citations={query.citations}
                />
              </BasicSystemUserMessage>
            </SystemMessageContainer>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  const builderStep = renderSegmentationBuilderStep(query);
  if (builderStep !== undefined) return builderStep;

  return undefined;
}

function renderSmartRoutingResponse(query: Chat.Query) {
  return (
    <SmartRoutingContainer
      state={query.state as SmartRoutingV2}
      queryId={query.id}
      queryIdentifier={query.identifier}
    />
  );
}

function renderRepResponses(query: Chat.Query, canRegenerate: boolean) {
  const responseType = query.response?.type;

  if (responseType === ChatResponseType.RepSearch) {
    return <RepSearchResponse disabled={!canRegenerate} />;
  }

  if (responseType === ChatResponseType.RepSearchResults) {
    return (
      <RepSearchResultsResponse
        queryIdentifier={query.identifier}
        response={query.response}
        disabled={!canRegenerate}
      />
    );
  }

  return undefined;
}

// The analytic planner's question and plan cards. Only the latest message's
// card can be answered.
function renderPlannerResponses(query: Chat.Query, canRegenerate: boolean) {
  const response = query.response;
  if (response?.type === ChatResponseType.PlannerClarify) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <PlannerClarify response={response} disabled={!canRegenerate} />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }
  if (response?.type === ChatResponseType.PlannerReview) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <PlannerReview response={response} disabled={!canRegenerate} />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }
  return undefined;
}

function renderAccountResponses(query: Chat.Query, canRegenerate: boolean) {
  const responseType = query.response?.type;

  if (responseType === ChatResponseType.AccountSearch) {
    return <AccountSearchResponse disabled={!canRegenerate} />;
  }

  if (responseType === ChatResponseType.AccountSearchResults) {
    return (
      <AccountSearchResultsResponse
        queryIdentifier={query.identifier}
        response={query.response}
        disabled={!canRegenerate}
      />
    );
  }

  return undefined;
}

export const QueryItem = memo(
  ({ canRegenerate, query }: Props) => {
    const renderSystemResponse = useCallback(() => {
      const responseType = query.response?.type;

      const customResult = renderCustomResponse(query);
      if (customResult !== undefined) return customResult;

      const hcpResult = renderHcpResponses(query, canRegenerate);
      if (hcpResult !== undefined) return hcpResult;

      const repResult = renderRepResponses(query, canRegenerate);
      if (repResult !== undefined) return repResult;

      const accountResult = renderAccountResponses(query, canRegenerate);
      if (accountResult !== undefined) return accountResult;

      const plannerResult = renderPlannerResponses(query, canRegenerate);
      if (plannerResult !== undefined) return plannerResult;

      const segResult = renderSegmentationResponses(query, canRegenerate);
      if (segResult !== undefined) return segResult;

      if (responseType === ChatResponseType.SmartRouting) {
        return renderSmartRoutingResponse(query);
      }

      return (
        <MessageContainer>
          <MessageResponse item={query} hasRegenerateFooter={canRegenerate} />
        </MessageContainer>
      );
    }, [query, canRegenerate]);

    const renderUserMessage = useCallback(() => {
      if (query.hideUserMessage) {
        return null;
      }

      const attachments = query.inputAttachments;

      return (
        <MessageContainer>
          <UserMessage
            value={
              <>
                {attachments && attachments.length > 0 && (
                  <InputAttachmentCards>
                    {attachments.map((a: InputAttachment) => (
                      <InputAttachmentCard key={a.identifier}>
                        <FileIcon extension={a.name.split('.').pop() ?? ''} size={28} />
                        <InputAttachmentCardBody>
                          <InputAttachmentCardName title={a.name}>{a.name}</InputAttachmentCardName>
                          <InputAttachmentCardSize>{formatBytes(a.sizeBytes)}</InputAttachmentCardSize>
                        </InputAttachmentCardBody>
                      </InputAttachmentCard>
                    ))}
                  </InputAttachmentCards>
                )}
                <BasicMarkdown>{query.value}</BasicMarkdown>
              </>
            }
          />
        </MessageContainer>
      );
    }, [query.value, query.hideUserMessage, query.inputAttachments]);

    return (
      <>
        {renderUserMessage()}
        {renderSystemResponse()}
      </>
    );
  },
  (prev, next) => prev.query === next.query && prev.canRegenerate === next.canRegenerate
);

type MessageResponseProps = {
  item: Chat.Query;
  hasRegenerateFooter: boolean;
};

// todo: possibly refactor to inverse the layout and have the current inner UI elements
// like variant selector and other actions contain the message response component
const MessageResponse = ({ item, hasRegenerateFooter }: MessageResponseProps) => {
  switch (item.status) {
    case ChatQueryStatus.LoadingExtended:
    case ChatQueryStatus.Loading: {
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <AgentProgressContainer queryIdentifier={item.identifier}>
              <SystemUserInFlightResponse />
            </AgentProgressContainer>
          </QueryEventsContainer>
        </QuerySocketContainer>
      );
    }

    case ChatQueryStatus.Error:
      return <SystemUserErrorMessage item={item} traceId={item.sentryTraceId} hasRegenerateFooter={false} />;

    case ChatQueryStatus.Streaming:
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <StreamingSystemUserMessage item={item} />
          </QueryEventsContainer>
        </QuerySocketContainer>
      );

    case ChatQueryStatus.Cancelled: {
      return <SystemUserCancelledMessage item={item} />;
    }

    case ChatQueryStatus.Complete:
    case ChatQueryStatus.FollowupsAvailable: {
      if (!item.response) {
        return null;
      }
      let chart: ChartData | null;
      if (item.response.type === ChatResponseType.Chart) {
        chart = item.response.data as ChartData;
      } else if (item.response.type === ChatResponseType.BIChartQuantAnalyses) {
        chart = item.response.data;
      } else {
        chart = null;
      }

      return (
        <AttachmentItemRendererContext.Provider value={AttachmentItem}>
          <AttachmentsContext.Provider value={item.attachments}>
            <QueryFollowupsContainer queryIdentifier={item.identifier} queryStatus={item.status}>
              <AudioStatusListener queryIdentifier={item.identifier} queryId={item.id}>
                <SystemMessageContainer citations={item.citations}>
                  <ChartContainer chart={chart}>
                    <AgentProgressContext.Provider value={item.agentProgress}>
                      <SystemUserMessage item={item} hasRegenerateFooter={hasRegenerateFooter} />
                    </AgentProgressContext.Provider>
                  </ChartContainer>
                </SystemMessageContainer>
              </AudioStatusListener>
            </QueryFollowupsContainer>
          </AttachmentsContext.Provider>
        </AttachmentItemRendererContext.Provider>
      );
    }
    // no default
  }
};

// Side Panel only: reduces the outer padding to match the Side Panel's tighter layout.
const MessageContainer = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const isSidePanel = useContext(IsSidePanelMessageContext);
  return (
    <MessageContainerRoot className={className} isSidePanel={isSidePanel}>
      {children}
    </MessageContainerRoot>
  );
};

const MessageContainerRoot = styled('div', { shouldForwardProp: shouldForwardProp('isSidePanel') })<{
  isSidePanel?: boolean;
}>(({ isSidePanel }) => ({
  paddingRight: isSidePanel ? 10 : 20,
  paddingLeft: isSidePanel ? 10 : 20,
}));

const InputAttachmentCards = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 8,
});

const InputAttachmentCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 8,
  border: `1px solid ${theme.palette.gray.light2}`,
  background: theme.palette.white.main,
  maxWidth: 200,
}));

const InputAttachmentCardBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const InputAttachmentCardName = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.black.light1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 130,
}));

const InputAttachmentCardSize = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.gray.dark1,
}));
